<template>
    <div class="payment-method">
        <!-- Debug: showTrialEndModal = {{ showTrialEndModal }} -->
        <TrialEndConfirmationModal
            v-if="showTrialEndModal"
            @confirm="confirmTrialEnd"
            @cancel="cancelTrialEnd"
        />
        <div class="payment-method__header">
            <div class="payment-method__title">{{ $t("billing.paymentMethodAndInvoiceAddress") }}</div>
        </div>
        <div class="payment-method__content">
            <div class="payment-card" ref="creditCard">
                <div class="payment-card__header">
                    <div class="header-content">
                        <div class="payment-card__title">{{ $t("billing.paymentMethod") }}</div>
                        <div class="iyzico-brand">
                            <IyzicoPay class="iyzico-logo" />
                        </div>
                    </div>
                </div>
                <div v-if="isEditingCreditCard || isSavedCreditCard" class="payment-card__content">
                    <div v-if="!isEditingCreditCard" class="payment-card__info">
                        <div class="card-info-left">
                            <DynamicSvg
                                :name="formatCardType(billingDetail.cardAssociation)"
                                class="payment-card__icon"
                            />
                            <div class="payment-card__details">
                                <div class="payment-card__number">{{ formatCardNumber(billingDetail.binNumber) }}</div>
                                <div class="payment-card__meta">
                                    <span class="card-type">{{ formatCardType(billingDetail.cardAssociation) }}</span>
                                </div>
                            </div>
                        </div>
                        <SupsisButton ghost classNames="action-button" @click="toggleCreditCardEditMode">
                            {{ $t("calendar.change") }}
                        </SupsisButton>
                    </div>

                    <div v-else class="credit-card-edit">
                        <div class="address-item full-width" :class="{ 'has-error': creditCardErrors.cardHolder }">
                            <a-tooltip placement="top">
                                <template slot="title">{{ $t("billing.cardHolderName") }}</template>
                                <a-icon type="user" />
                            </a-tooltip>
                            <a-input
                                v-model="editableCreditCard.cardHolder"
                                autocomplete="cc-name"
                                name="cardholderName"
                                id="cardholderName"
                                @input="handleCreditCardInput('cardHolder')"
                                data-field="cardHolder"
                                class="edit-input large-font"
                                :placeholder="$t('billing.cardHolderName')"
                            />
                        </div>

                        <div class="credit-card-columns">
                            <div class="address-item" :class="{ 'has-error': creditCardErrors.cardNumber }">
                                <a-tooltip placement="top">
                                    <template slot="title">{{ $t("billing.cardNumber") }}</template>
                                    <a-icon type="credit-card" />
                                </a-tooltip>
                                <a-input
                                    v-model="editableCreditCard.cardNumber"
                                    autocomplete="cc-number"
                                    name="cardNumber"
                                    id="cardNumber"
                                    inputmode="numeric"
                                    v-mask="'#### #### #### ####'"
                                    data-field="cardNumber"
                                    class="edit-input large-font"
                                    placeholder="**** **** **** ****"
                                    type="tel"
                                />
                            </div>

                            <div class="address-item small-input" :class="{ 'has-error': creditCardErrors.expiry }">
                                <a-tooltip placement="top">
                                    <template slot="title">{{ $t("billing.expireDate") }}</template>
                                    <a-icon type="calendar" />
                                </a-tooltip>
                                <a-input
                                    v-model="editableCreditCard.expiry"
                                    autocomplete="cc-exp"
                                    name="ccExp"
                                    id="ccExp"
                                    inputmode="numeric"
                                    pattern="(0[1-9]|1[0-2])\/?([0-9]{2})"
                                    v-mask="'##/##'"
                                    data-field="expiry"
                                    class="edit-input"
                                    placeholder="MM/YY*"
                                    type="tel"
                                />
                            </div>

                            <div class="address-item small-input" :class="{ 'has-error': creditCardErrors.cvc }">
                                <a-tooltip placement="top">
                                    <template slot="title">CVC</template>
                                    <a-icon type="safety-certificate" />
                                </a-tooltip>
                                <a-input
                                    v-model="editableCreditCard.cvc"
                                    autocomplete="cc-csc"
                                    name="cvc"
                                    id="cvc"
                                    inputmode="numeric"
                                    pattern="\d*"
                                    v-mask="'###'"
                                    data-field="cvc"
                                    class="edit-input"
                                    placeholder="***"
                                    type="tel"
                                    maxlength="4"
                                />
                            </div>
                        </div>

                        <div class="address-actions">
                            <SupsisButton 
                                ghost 
                                classNames="action-button" 
                                @click="cancelCreditCardEdit"
                                :disabled="isSavingCreditCard"
                            >
                                {{ $t("billing.cancel") }}
                            </SupsisButton>
                            <SupsisButton 
                                primary 
                                classNames="action-button" 
                                @click.stop="saveCreditCard"
                                :disabled="isSavingCreditCard"
                            >
                                <a-spin v-if="isSavingCreditCard" size="small" style="margin-right: 8px;" />
                                {{ $t("billing.save") }}
                            </SupsisButton>
                        </div>
                    </div>
                    <div class="payment-card__notice">
                        <a-icon type="lock" theme="filled" />
                        <span>{{ $t("billing.cardInfoIsStoredSecurelyByIyzico") }}</span>
                    </div>
                </div>
                <div v-else-if="!isEditingCreditCard" class="empty-state">
                    <span class="material-icons">credit_card_off</span>
                    <p>{{ $t("billing.notFoundSavedCard") }}</p>
                    <SupsisButton primary classNames="action-button" @click="toggleCreditCardEditMode">
                        {{ $t("settings.newAdd") }}
                    </SupsisButton>
                </div>
            </div>

            <div class="payment-card">
                <div class="payment-card__header">
                    <div class="payment-card__title">{{ $t("billing.invoiceAddress") }}</div>
                </div>
                <div v-if="isEditingBillingAddress || billingDetail.name" class="payment-card__content">
                    <div v-if="!isEditingBillingAddress" class="address-info">
                        <div class="address-columns">
                            <div class="address-item">
                                <a-tooltip placement="top">
                                    <template slot="title">
                                        {{
                                            billingDetail.billingType === "company"
                                                ? $t("billing.companyName")
                                                : $t("automation.nameSurname")
                                        }}
                                    </template>
                                    <a-icon :type="billingDetail.billingType === 'company' ? 'shop' : 'user'" />
                                </a-tooltip>
                                <span class="address-value">
                                    {{ billingDetail.name }}
                                    <template v-if="billingDetail.billingType !== 'company'">
                                        {{ billingDetail.surname }}
                                    </template>
                                </span>
                            </div>

                            <div class="address-item">
                                <a-tooltip placement="top">
                                    <template slot="title">{{ $t("calendar.emailAddress") }}</template>
                                    <a-icon type="mail" />
                                </a-tooltip>
                                <span class="address-value">{{ billingDetail.email }}</span>
                            </div>
                        </div>

                        <div class="address-columns">
                            <div class="address-item" v-if="billingDetail.billingType !== 'company'">
                                <a-tooltip placement="top">
                                    <template slot="title">{{ $t("probel.identityNumber") }}</template>
                                    <a-icon type="audit" />
                                </a-tooltip>
                                <span class="address-value">{{ billingDetail.identityNumber }}</span>
                            </div>
                            <div class="address-item" v-if="billingDetail.billingType === 'company'">
                                <a-tooltip placement="top">
                                    <template slot="title">{{ $t("billing.taxOffice") }}</template>
                                    <a-icon type="audit" />
                                </a-tooltip>
                                <span class="address-value">{{ billingDetail.taxOffice || "Belirtilmemiş" }}</span>
                            </div>

                            <div class="address-item" v-if="billingDetail.billingType === 'company'">
                                <a-tooltip placement="top">
                                    <template slot="title">{{ $t("billing.taxNumber") }}</template>
                                    <a-icon type="number" />
                                </a-tooltip>
                                <span class="address-value">{{ billingDetail.taxNumber || "Belirtilmemiş" }}</span>
                            </div>
                        </div>

                        <div class="address-columns">
                            <div class="address-item">
                                <a-tooltip placement="top">
                                    <template slot="title">{{ $t("billing.billingCountry") }}</template>
                                    <a-icon type="global" />
                                </a-tooltip>
                                <span class="address-value">{{ billingDetail.billingCountry }}</span>
                            </div>

                            <div class="address-item">
                                <a-tooltip placement="top">
                                    <template slot="title">{{ $t("billing.billingCity") }}</template>
                                    <a-icon type="environment" />
                                </a-tooltip>
                                <span class="address-value">{{ billingDetail.billingCity }}</span>
                            </div>
                        </div>

                        <div class="address-item full-width">
                            <a-tooltip placement="top">
                                <template slot="title">{{ $t("message.address") }}</template>
                                <a-icon type="home" />
                            </a-tooltip>
                            <span class="address-value">{{ billingDetail.billingAddress }}</span>
                        </div>

                        <div class="address-actions">
                            <SupsisButton primary classNames="action-button" @click="toggleEditMode">
                                {{ $t("billing.updateBillingInfo") }}
                            </SupsisButton>
                        </div>
                    </div>

                    <div v-else class="address-info">
                        <div class="address-item full-width">
                            <div class="billing-type-container">
                                <span class="billing-type-label">Fatura Türü:</span>
                                <a-switch
                                    v-model="editableBillingDetail.isCompany"
                                    checked-children="Şirket"
                                    un-checked-children="Bireysel"
                                    class="billing-type-switch"
                                />
                            </div>
                        </div>

                        <div class="address-columns">
                            <div
                                class="address-item"
                                :class="{ 'has-error': billingErrors.companyName }"
                                v-if="editableBillingDetail.isCompany"
                            >
                                <a-tooltip placement="top">
                                    <template slot="title">{{ $t("billing.companyName") }}</template>
                                    <a-icon type="shop" />
                                </a-tooltip>
                                <a-input
                                    v-model="editableBillingDetail.companyName"
                                    @input="handleInput('companyName')"
                                    data-field="companyName"
                                    class="edit-input"
                                    placeholder="Şirket Adı*"
                                    required
                                />
                            </div>
                            <div class="address-item" :class="{ 'has-error': billingErrors.fullName }" v-else>
                                <a-tooltip placement="top">
                                    <template slot="title">{{ $t("automation.nameSurname") }}</template>
                                    <a-icon type="user" />
                                </a-tooltip>
                                <a-input
                                    v-model="editableBillingDetail.fullName"
                                    @input="handleInput('fullName')"
                                    data-field="fullName"
                                    class="edit-input"
                                    placeholder="Ad Soyad*"
                                    required
                                />
                            </div>
                            <div class="address-item" :class="{ 'has-error': billingErrors.email }">
                                <a-tooltip placement="top">
                                    <template slot="title">{{ $t("calendar.emailAddress") }}</template>
                                    <a-icon type="mail" />
                                </a-tooltip>
                                <a-input
                                    v-model="editableBillingDetail.email"
                                    @input="handleInput('email')"
                                    data-field="email"
                                    class="edit-input"
                                    type="email"
                                    placeholder="E-Posta*"
                                    required
                                />
                            </div>
                        </div>

                        <div class="address-columns">
                            <div
                                class="address-item"
                                :class="{ 'has-error': billingErrors.identityNumber }"
                                v-if="!editableBillingDetail.isCompany"
                            >
                                <a-tooltip placement="top">
                                    <template slot="title">{{ $t("fastWizard.probel.identityNumber") }}</template>
                                    <a-icon type="idcard" />
                                </a-tooltip>
                                <a-input
                                    v-model="editableBillingDetail.identityNumber"
                                    @input="handleInput('identityNumber')"
                                    data-field="identityNumber"
                                    class="edit-input"
                                    placeholder="TC Kimlik Numarası*"
                                    required
                                />
                            </div>
                            <div class="address-item" :class="{ 'has-error': billingErrors.taxNumber }" v-else>
                                <a-tooltip placement="top">
                                    <template slot="title">{{ $t("billing.taxNumber") }}</template>
                                    <a-icon type="number" />
                                </a-tooltip>
                                <a-input
                                    v-model="editableBillingDetail.taxNumber"
                                    @input="handleInput('taxNumber')"
                                    data-field="taxNumber"
                                    class="edit-input"
                                    placeholder="Vergi Numarası*"
                                    required
                                />
                            </div>
                            <div
                                class="address-item"
                                :class="{ 'has-error': billingErrors.taxOffice }"
                                v-if="editableBillingDetail.isCompany"
                            >
                                <a-tooltip placement="top">
                                    <template slot="title">{{ $t("billing.taxOffice") }}</template>
                                    <a-icon type="audit" />
                                </a-tooltip>
                                <a-input
                                    v-model="editableBillingDetail.taxOffice"
                                    @input="handleInput('taxOffice')"
                                    data-field="taxOffice"
                                    class="edit-input"
                                    placeholder="Vergi Dairesi*"
                                    required
                                />
                            </div>
                        </div>

                        <div class="address-columns">
                            <div class="address-item">
                                <a-tooltip placement="top">
                                    <template slot="title">{{ $t("billing.billingCountry") }}</template>
                                    <a-icon type="global" />
                                </a-tooltip>
                                <a-input
                                    v-model="editableBillingDetail.billingCountry"
                                    @input="handleInput('billingCountry')"
                                    data-field="billingCountry"
                                    class="edit-input"
                                    placeholder="Ülke*"
                                    required
                                />
                            </div>
                            <div class="address-item">
                                <a-tooltip placement="top">
                                    <template slot="title">{{ $t("billing.billingCity") }}</template>
                                    <a-icon type="environment" />
                                </a-tooltip>
                                <a-input
                                    v-model="editableBillingDetail.billingCity"
                                    @input="handleInput('billingCity')"
                                    data-field="billingCity"
                                    class="edit-input"
                                    placeholder="Şehir*"
                                    required
                                />
                            </div>
                        </div>

                        <div class="address-item flex-start-item full-width">
                            <a-tooltip placement="top">
                                <template slot="title">{{ $t("message.address") }}</template>
                                <a-icon type="home" />
                            </a-tooltip>
                            <a-textarea
                                v-model="editableBillingDetail.billingAddress"
                                @input="handleInput('billingAddress')"
                                data-field="billingAddress"
                                class="edit-input"
                                placeholder="Tam Adres*"
                                :rows="3"
                                required
                            />
                        </div>

                        <div class="address-actions">
                            <SupsisButton ghost classNames="action-button" @click="cancelEdit">
                                {{ $t("billing.cancel") }}
                            </SupsisButton>
                            <SupsisButton primary classNames="action-button" @click="saveBillingAddress">
                                {{ $t("billing.save") }}
                            </SupsisButton>
                        </div>
                    </div>
                </div>
                <div v-else class="empty-state">
                    <span class="material-icons">location_off</span>
                    <p>{{ $t("billing.notFoundSavedBillingAddress") }}</p>
                    <SupsisButton primary classNames="action-button" @click="toggleEditMode">
                        {{ $t("settings.newAdd") }}
                    </SupsisButton>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import _ from "lodash";
import PlanItem from "./PlanItem.vue";
import IyzicoPay from "../../../../components/svg/IyzicoPay.vue";
import DynamicSvg from "../../../../components/DynamicSvg.vue";
import SupsisButton from "@/views/Pages/Settings/components/SupsisButton.vue";
import TrialEndConfirmationModal from "../../../../components/billing/TrialEndConfirmationModal.vue";

export default {
    name: "PaymentMethod",
    components: { DynamicSvg, IyzicoPay, PlanItem, SupsisButton, TrialEndConfirmationModal },
    computed: {
        ...mapGetters({
            isTrialMode: "site/isTrialMode",
            isSavedCreditCard: "site/isSavedCreditCard",
        }),
        ...mapState({
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
            billingDetail: (state) => state.site.billingDetail || {},
        }),
    },
    data() {
        return {
            isCreditCardModalVisible: false,
            isBillingAddressModalVisible: false,
            creditCard: null,
            billingAddress: null,
            isEditingBillingAddress: false,
            editableBillingDetail: {
                isCompany: false,
                companyName: "",
                email: "",
                taxOffice: "",
                taxNumber: "",
                billingCountry: "",
                billingCity: "",
                billingAddress: "",
                fullName: "",
                identityNumber: "",
            },
            billingErrors: {},
            isEditingCreditCard: false,
            editableCreditCard: {
                cardNumber: "",
                expiry: "",
                cvc: "",
                cardHolder: "",
            },
            creditCardErrors: {},
            requiredCardFocus: this.$route.query.focus === "creditCard",
            showTrialEndModal: false,
            // Kart kaydetme işlemi için loading state
            isSavingCreditCard: false,
        };
    },
    mounted() {
        if (this.requiredCardFocus) this.focusCreditCardForm();
    },
    methods: {
        focusCreditCardForm() {
            this.$nextTick(() => {
                const creditCardElement = this.$refs.creditCard;
                if (creditCardElement) {
                    creditCardElement.scrollIntoView({ behavior: "smooth", block: "center" });
                    setTimeout(() => creditCardElement.classList.add("focused"), 0);
                    this.requiredCardFocus = false;
                    this.$router.replace({ query: { ...this.$route.query, focus: undefined } });
                }
            });
        },
        formatCardType(type) {
            if (!type) return "";
            const formatted = type.split("_").join(" ");
            return formatted.charAt(0).toUpperCase() + formatted.slice(1).toLowerCase();
        },
        formatCardNumber(number) {
            if (!number) return "";
            const firstEight = number.toString().substring(0, 8);
            return `${firstEight}********`.match(/.{1,4}/g).join(" ");
        },
        toggleEditMode(defaultValue) {
            this.isEditingBillingAddress = defaultValue !== undefined ? defaultValue : !this.isEditingBillingAddress;
            if (this.isEditingBillingAddress) {
                this.editableBillingDetail = this.billingDetail.name
                    ? _.cloneDeep({
                          ...this.billingDetail,
                          isCompany: this.billingDetail.billingType === "company",
                          companyName: this.billingDetail.name,
                          fullName: [this.billingDetail.name, this.billingDetail.surname].join(" ").trim(),
                      })
                    : {
                          isCompany: false,
                          companyName: "",
                          email: "",
                          taxOffice: "",
                          taxNumber: "",
                          billingCountry: "Türkiye",
                          billingCity: "",
                          billingAddress: "",
                          fullName: "",
                          identityNumber: "",
                      };
                this.billingErrors = {};
            }
        },
        cancelEdit() {
            this.isEditingBillingAddress = false;
            this.editableBillingDetail = {
                isCompany: false,
                companyName: "",
                email: "",
                taxOffice: "",
                taxNumber: "",
                billingCountry: "",
                billingCity: "",
                billingAddress: "",
                fullName: "",
                identityNumber: "",
            };
        },
        async saveBillingAddress() {
            this.billingErrors = {};
            if (this.editableBillingDetail.isCompany) {
                this.editableBillingDetail.name = this.editableBillingDetail.companyName;
                this.editableBillingDetail.surname = "Company";
            } else {
                const nameParts = this.editableBillingDetail.fullName.trim().split(" ").filter(Boolean);
                if (nameParts.length < 2) {
                    this.$set(this.billingErrors, "fullName", true);
                    this.$message.error("Lütfen geçerli bir isim ve soyisim giriniz");
                    this.focusFirstErrorField(["fullName"]);
                    return;
                }
                this.editableBillingDetail.name = nameParts[0];
                this.editableBillingDetail.surname = nameParts.slice(1).join(" ");
            }
            const requiredFields = this.editableBillingDetail.isCompany
                ? ["companyName", "email", "taxOffice", "taxNumber", "billingCountry", "billingCity", "billingAddress"]
                : ["fullName", "email", "identityNumber", "billingCountry", "billingCity", "billingAddress"];

            let hasError = false;

            requiredFields.forEach((field) => {
                if (field === "fullName") {
                    const nameParts = this.editableBillingDetail.fullName.trim().split(" ").filter(Boolean);
                    if (nameParts.length < 2) {
                        this.billingErrors.fullName = true;
                        hasError = true;
                    }
                } else if (!this.editableBillingDetail[field]) {
                    this.billingErrors[field] = true;
                    hasError = true;
                }
            });

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(this.editableBillingDetail.email)) {
                this.billingErrors.email = true;
                hasError = true;
            }

            if (hasError) {
                this.$message.error("Zorunlu alanları kontrol ediniz");
                this.focusFirstErrorField(requiredFields);
                return;
            }

            try {
                await this.$store.dispatch("site/updateBilling", {
                    ...this.editableBillingDetail,
                    billingType: this.editableBillingDetail.isCompany ? "company" : "individual",
                });
                this.$notification.success({
                    message: "Başarılı",
                    description: "Fatura bilgileriniz başarıyla güncellendi",
                    duration: 4.5,
                });
                this.cancelEdit();
            } catch (error) {
                this.$notification.error({
                    message: "Hata",
                    description: "Fatura bilgileri güncellenirken bir hata oluştu",
                    duration: 4.5,
                });
            }
        },
        focusFirstErrorField(fields) {
            const firstErrorField = fields.find((f) => this.billingErrors[f]);
            if (firstErrorField) {
                const errorElement = document.querySelector(`[data-field="${firstErrorField}"]`);
                if (errorElement) {
                    errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
                    errorElement.focus({ preventScroll: true });
                }
            }
        },
        handleInput(field) {
            if (field === "fullName" && this.billingErrors.fullName) {
                const nameParts = this.editableBillingDetail.fullName.split(" ").filter(Boolean);
                if (nameParts.length >= 2) {
                    this.$delete(this.billingErrors, "fullName");
                    this.$message.destroy();
                }
            }
            if (this.billingErrors[field]) {
                this.$delete(this.billingErrors, field);
                this.$message.destroy();
            }
        },
        toggleCreditCardEditMode() {
            if (!this.hasBillingAddress()) {
                this.$notification.warning({
                    message: "Eksik Bilgi",
                    description: (h) => (
                        <div>
                            <p>Kart ekleyebilmek için önce fatura adresi bilgilerinizi eklemelisiniz</p>
                            <SupsisButton
                                type="link"
                                primary
                                classNames="action-button"
                                onClick={() => {
                                    this.toggleEditMode(true);
                                    this.$notification.destroy();
                                }}
                            >
                                Fatura Adresi Ekle
                            </SupsisButton>
                        </div>
                    ),
                    duration: 0,
                    icon: <a-icon type="info-circle" theme="filled" style="color: #1890ff" />,
                });
                return;
            }

            this.isEditingCreditCard = !this.isEditingCreditCard;
            if (this.isEditingCreditCard) {
                this.editableCreditCard = this.billingDetail.cardAssociation
                    ? _.cloneDeep(this.billingDetail)
                    : {
                          cardNumber: "",
                          expiry: "",
                          cvc: "",
                          cardHolder: "",
                      };
                this.creditCardErrors = {};
            }
        },
        hasBillingAddress() {
            return (
                this.billingDetail &&
                (this.billingDetail.name || this.billingDetail.companyName || this.billingDetail.billingAddress)
            );
        },
        cancelCreditCardEdit() {
            this.isEditingCreditCard = false;
            this.editableCreditCard = {
                cardNumber: "",
                expiry: "",
                cvc: "",
                cardHolder: "",
            };
        },
        /**
         * Kredi kartı kaydetme işlemini başlatır
         * API çağrısı tamamlanana kadar buton disabled kalır
         */
        async saveCreditCard() {
            // Eğer zaten kaydetme işlemi devam ediyorsa, tekrar tıklamayı engelle
            if (this.isSavingCreditCard) {
                return;
            }

            const validationResult = this.validateCreditCard();
            console.log("validationResult", validationResult);
            if (!validationResult.isValid) {
                return;
            }

            console.log("isTrialMode", this.isTrialMode);
            if (this.isTrialMode) {
                this.$nextTick(() => {
                    this.showTrialEndModal = true;
                });
                return;
            }

            await this.processCreditCardSave();
        },
        validateCreditCard() {
            const requiredFields = ["cardNumber", "expiry", "cvc", "cardHolder"];
            this.creditCardErrors = {};
            let hasError = false;

            requiredFields.forEach((field) => {
                if (!this.editableCreditCard[field]) {
                    this.creditCardErrors[field] = true;
                    hasError = true;
                }
            });

            const expiryRegex = /^(0[1-9]|1[0-2])\/([0-9]{2})$/;
            if (this.editableCreditCard.expiry && !expiryRegex.test(this.editableCreditCard.expiry)) {
                this.creditCardErrors.expiry = true;
                hasError = true;
            }

            const cleanedCardNumber = this.editableCreditCard.cardNumber.replace(/\s/g, "");
            if (this.editableCreditCard.cardNumber && !/^\d{16}$/.test(cleanedCardNumber)) {
                this.creditCardErrors.cardNumber = true;
                hasError = true;
            }

            if (this.editableCreditCard.cvc && this.editableCreditCard.cvc.length < 3) {
                this.creditCardErrors.cvc = true;
                hasError = true;
            }

            if (hasError) {
                this.$message.error("Lütfen geçerli kart bilgileri giriniz");
                this.focusFirstErrorField(requiredFields);
            }

            return {
                isValid: !hasError,
                errors: this.creditCardErrors
            };
        },
        /**
         * Kredi kartı bilgilerini API'ye kaydeder
         * API çağrısı tamamlanana kadar loading state aktif kalır
         */
        async processCreditCardSave() {
            // Loading state'ini aktif et
            this.isSavingCreditCard = true;

            try {
                const cleanedCardNumber = this.editableCreditCard.cardNumber.replace(/\s/g, "");
                const [expireMonth, expireYear] = this.editableCreditCard.expiry.split("/");

                const payload = {
                    alias: this.editableCreditCard.cvc,
                    holderName: this.editableCreditCard.cardHolder,
                    cardNumber: cleanedCardNumber,
                    expireMonth: expireMonth,
                    expireYear: "20" + expireYear,
                };

                const result = await this.$store.dispatch("site/updateCardInfo", payload);
                if (result.status === "error") {
                    this.$notification.error({
                        message: "Hata",
                        description: result.message,
                        duration: 4.5,
                    });
                    return;
                } else {
                    this.$notification.success({
                        message: "Başarılı",
                        description: "Kart bilgileriniz başarıyla güncellendi",
                        duration: 4.5,
                    });
                    this.cancelCreditCardEdit();
                }
            } catch (error) {
                console.error("Kart kaydetme hatası:", error);
                this.$notification.error({
                    message: "Hata",
                    description: "Kart bilgileri güncellenirken bir hata oluştu",
                    duration: 4.5,
                });
            } finally {
                // API çağrısı tamamlandığında (başarılı veya hatalı) loading state'ini kaldır
                this.isSavingCreditCard = false;
            }
        },
        focusFirstErrorField(fields) {
            const firstErrorField = fields.find((f) => this.creditCardErrors[f]);
            if (firstErrorField) {
                const errorElement = document.querySelector(`[data-field="${firstErrorField}"]`);
                if (errorElement) {
                    errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
                    errorElement.focus({ preventScroll: true });
                }
            }
        },
        handleCreditCardInput(field) {
            if (this.creditCardErrors[field]) {
                this.$delete(this.creditCardErrors, field);
                this.$message.destroy();
            }
        },
        /**
         * Trial bitiş onayından sonra kart kaydetme işlemini devam ettirir
         */
        async confirmTrialEnd() {
            this.showTrialEndModal = false;
            await this.processCreditCardSave();
        },
        cancelTrialEnd() {
            this.showTrialEndModal = false;
        },

    },
};
</script>

<style lang="scss" scoped>
.payment-method {
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    padding: 20px;

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }

    &__title {
        font-size: 20px;
        font-weight: 600;
        color: #1c1b1f;
    }

    &__content {
        background: #f1f4f7;
        border-radius: 8px;
        padding: 10px;
    }
}

.payment-card {
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 10px;
    border: 1px solid #e8e8e8;
    transition: box-shadow 0.3s;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }

    &__header {
        margin-bottom: 24px;
        padding-bottom: 16px;
        border-bottom: 1px solid #edf2f7;

        .header-content {
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
    }

    &__title {
        font-size: 18px;
        font-weight: 600;
        color: #1a202c;
        letter-spacing: -0.2px;
        display: flex;
        align-items: center;
        gap: 8px;

        &::before {
            content: "";
            display: block;
            width: 3px;
            height: 20px;
            background: #2f54eb;
            border-radius: 2px;
        }
    }

    &__content {
        padding: 16px 0;
    }

    &__info {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .card-info-left {
            display: flex;
            align-items: center;
            gap: 15px;
        }
    }

    &__number {
        font-size: 18px;
        letter-spacing: 1.2px;
        color: #2d3748;
    }

    &__meta {
        display: flex;
        gap: 15px;
        font-size: 14px;

        .card-type {
            color: #4a5568;
        }
    }

    &__notice {
        margin-top: 20px;
        padding-top: 16px;
        border-top: 1px solid #edf2f7;
        display: flex;
        align-items: center;
        gap: 8px;
        color: #38a169;
        font-size: 14px;
    }

    &__icon {
        width: 48px;
        height: 32px;
        object-fit: contain;
    }

    .iyzico-brand {
        display: flex;
        align-items: center;
        gap: 8px;

        .iyzico-text {
            font-size: 13px;
        }

        .iyzico-logo {
            width: 80px;
            height: 24px;
        }
    }
}

.address-info {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.address-columns {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
}

.address-item {
    display: flex;
    align-items: center;
    padding: 12px;
    background: #f8fafc;
    border-radius: 6px;

    &.flex-start-item {
        align-items: flex-start;
    }

    .anticon {
        color: #667eea;
        font-size: 16px;
        margin-top: 2px;
    }
}

.address-value {
    font-size: 14px;
    line-height: 1.5;
    color: #2d3748;
}

.action-button {
    border-radius: 6px;
    font-weight: 500;
    padding: 0 20px;
    height: 36px;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 24px;
    text-align: center;

    .action-button {
        margin-top: 10px;
    }
}

.address-actions {
    margin-top: 20px;
    border-top: 1px solid #edf2f7;
    padding-top: 16px;
    display: flex;
    justify-content: flex-end;
    gap: 5px;
}

.payment-card__meta {
    .card-expiry {
        display: none;
    }
}

.address-item {
    .anticon {
        transition: color 0.3s;

        &:hover {
            color: #2f54eb;
        }
    }
}

.full-width {
    grid-column: 1 / -1;
}

.address-value {
    word-break: break-word;
    padding-right: 8px;
}

.address-item {
    .edit-input,
    .edit-select {
        background: #f8fafc !important;
        border: none !important;
        box-shadow: none !important;
        padding: 0 8px;

        &:focus {
            box-shadow: 0 0 0 2px rgba(47, 84, 235, 0.2) !important;
        }

        &::placeholder {
            color: #a0aec0;
            font-style: italic;
        }
    }

    .ant-input-prefix {
        margin-right: 8px;
    }

    .anticon {
        margin-right: 12px;
        color: #667eea;
        flex-shrink: 0;
    }
}

.edit-select {
    .ant-select-selection {
        background: #f8fafc !important;
        border: none !important;
        box-shadow: none !important;
    }
}

.billing-type-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 8px 0;

    .billing-type-label {
        font-weight: 500;
        color: #2d3748;
    }

    .billing-type-switch {
        background-color: #e2e8f0;

        &.ant-switch-checked {
            background-color: #2f54eb;
        }

        .ant-switch-inner {
            color: #fff;
            font-size: 12px;
            padding: 0 6px;
        }
    }
}

.has-error {
    .edit-input,
    .edit-select,
    .ant-input {
        border: 1px solid #ff4d4f !important;
        background-color: #fff1f0 !important;
    }

    .anticon {
        color: #ff4d4f !important;
    }
}

.address-item {
    .edit-input::placeholder {
        position: relative;

        &:after {
            content: "*";
            color: #ff4d4f;
            margin-left: 2px;
        }
    }
}

.credit-card-columns {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    gap: 16px;
    margin: 12px 0;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
}

.large-font {
    .edit-input {
        font-size: 18px;
        letter-spacing: 1.2px;
    }
}

.small-input {
    .edit-input {
        font-size: 14px;
        padding: 8px 12px;
    }
}

.address-item {
    .anticon {
        margin-right: 12px;
        font-size: 16px;
    }

    &.full-width {
        margin-bottom: 16px;
    }
}
</style>
